import { Link } from "react-router-dom";
import { myStyleDark, myStyleLight } from "./helpers";
import About from "../routes/About";
import {
  BulbOutlined,
  CodeOutlined,
  CloudOutlined,
  CheckCircleOutlined,
  ArrowRightOutlined,
  RocketOutlined,
} from "@ant-design/icons";
import codingCafe from "../images/coding-cafe.jpg";

const stats = [
  { value: "10+", label: "Years of delivery experience" },
  { value: "40+", label: "Successful product launches" },
  { value: "99.9%", label: "Uptime targets met" },
];

const services = [
  {
    icon: <BulbOutlined />,
    iconBg: "rgba(59, 130, 246, 0.12)",
    iconColor: "#3b82f6",
    title: "Product Strategy",
    body: "Roadmaps, scope definition, and ROI analysis to align your engineering investment with real business outcomes.",
  },
  {
    icon: <CodeOutlined />,
    iconBg: "rgba(6, 182, 212, 0.12)",
    iconColor: "#06b6d4",
    title: "Custom Development",
    body: "Modern web apps, APIs, and integrations built for performance, security, and long-term maintainability.",
  },
  {
    icon: <CloudOutlined />,
    iconBg: "rgba(139, 92, 246, 0.12)",
    iconColor: "#8b5cf6",
    title: "Cloud & DevOps",
    body: "Infrastructure automation, CI/CD pipelines, and observability to keep your systems reliable at any scale.",
  },
];

const techStack = [
  { label: "React & TypeScript", color: "#38bdf8", bg: "rgba(56, 189, 248, 0.10)" },
  { label: "Node.js & Python",   color: "#4ade80", bg: "rgba(74, 222, 128, 0.10)" },
  { label: "AWS & Azure",        color: "#fb923c", bg: "rgba(251, 146, 60, 0.10)"  },
  { label: "PostgreSQL & Redis", color: "#818cf8", bg: "rgba(129, 140, 248, 0.10)" },
  { label: "Docker & Kubernetes",color: "#22d3ee", bg: "rgba(34, 211, 238, 0.10)"  },
  { label: "REST & GraphQL",     color: "#f472b6", bg: "rgba(244, 114, 182, 0.10)" },
];

const steps = [
  {
    num: "1",
    title: "Discover",
    body: "We learn your business, define clear requirements, and validate success metrics before a line of code is written.",
  },
  {
    num: "2",
    title: "Build",
    body: "We design and implement with weekly demos, transparent progress, and predictable delivery milestones.",
  },
  {
    num: "3",
    title: "Scale",
    body: "We harden, monitor, and optimize your product so it grows confidently as your business demands increase.",
  },
];

const outcomes = [
  {
    icon: "📈",
    metric: "35% faster",
    label: "user onboarding after redesigning a SaaS platform's activation flow",
  },
  {
    icon: "☁️",
    metric: "Cloud-native",
    label: "migration of a legacy monolith to microservices with zero downtime",
  },
  {
    icon: "🔒",
    metric: "+18% retention",
    label: "lift for a customer portal rebuild focused on reliability and UX",
  },
];

const deliverables = [
  "Product and technical strategy",
  "Full-stack engineering teams",
  "Cloud architecture and DevOps",
  "Ongoing support and optimization",
];

const sectionStyle  = (dark) => (dark ? myStyleDark : myStyleLight);
const textClass     = (dark) => (dark ? "text-white" : "text-black");
const mutedClass    = (dark) => (dark ? "text-white-50" : "text-muted");

export const LandingPage = (finalIsDarkMode) => {
  const surface = sectionStyle(finalIsDarkMode);
  const tc      = textClass(finalIsDarkMode);
  const mc      = mutedClass(finalIsDarkMode);

  const pageBg = finalIsDarkMode
    ? { background: "#0d1526", minHeight: "100vh" }
    : { background: "#f1f5f9", minHeight: "100vh" };

  return (
    <div style={pageBg}>

      {/* ─── HERO ──────────────────────────────────────────────── */}
      <div className="hero-section">
        <div
          className="hero-bg-image"
          style={{ backgroundImage: `url(${codingCafe})` }}
        />
        <div className="hero-overlay" />

        <div className="container hero-content">
          <div className="row align-items-center g-5">

            <div className="col-lg-7">
              <div className="hero-badge animate-fade-up">
                <span className="hero-badge-dot" />
                Available for new projects
              </div>

              <h1
                className="display-3 fw-bold text-white mb-4 animate-fade-up anim-delay-1"
                style={{ lineHeight: 1.15 }}
              >
                We build software that{" "}
                <span className="gradient-text">drives growth</span>
              </h1>

              <p
                className="lead text-white-50 mb-5 animate-fade-up anim-delay-2"
                style={{ maxWidth: 540, fontSize: "1.15rem", lineHeight: 1.75 }}
              >
                Full-stack consulting from strategy to deployment. We ship
                high-quality products on time, on budget, and built to scale.
              </p>

              <div className="d-flex gap-3 flex-wrap animate-fade-up anim-delay-3">
                <a
                  className="btn btn-primary btn-lg px-4"
                  href="mailto:dilks44@gmail.com"
                  style={{ borderRadius: 10, fontWeight: 600 }}
                >
                  Schedule a consultation
                </a>
                <Link
                  className="btn btn-outline-light btn-lg px-4"
                  to="/insights"
                  style={{ borderRadius: 10 }}
                >
                  View insights &nbsp;<ArrowRightOutlined />
                </Link>
              </div>
            </div>

            <div className="col-lg-5 d-none d-lg-flex justify-content-end animate-fade-up anim-delay-4">
              <div className="hero-deliverables" style={{ width: "100%", maxWidth: 340 }}>
                <h5 className="text-white fw-semibold mb-4">What we deliver</h5>
                {deliverables.map((item) => (
                  <div key={item} className="d-flex align-items-center gap-3 mb-3">
                    <CheckCircleOutlined style={{ color: "#22d3ee", fontSize: 17, flexShrink: 0 }} />
                    <span className="text-white-50" style={{ fontSize: 18, lineHeight: 1.5 }}>
                      {item}
                    </span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* ─── STATS ─────────────────────────────────────────────── */}
      <div className="container" style={{ marginTop: -48, position: "relative", zIndex: 10 }}>
        <div className="row g-3">
          {stats.map((stat) => (
            <div className="col-md-4" key={stat.label}>
              <div
                className="p-4 rounded-3 h-100 stat-card text-center"
                style={{ ...surface, borderTop: "3px solid #3b82f6" }}
              >
                <div
                  className="fw-bold mb-1"
                  style={{
                    fontSize: "2.2rem",
                    background: "linear-gradient(135deg, #3b82f6, #22d3ee)",
                    WebkitBackgroundClip: "text",
                    WebkitTextFillColor: "transparent",
                    backgroundClip: "text",
                  }}
                >
                  {stat.value}
                </div>
                <div className={mc} style={{ fontSize: 18 }}>{stat.label}</div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── SERVICES ──────────────────────────────────────────── */}
      <div className="container py-5 mt-3">
        <div className="text-center mb-5">
          <span className="section-label">Services</span>
          <h2 className={`fw-bold ${tc}`} style={{ fontSize: "2rem" }}>
            What we offer
          </h2>
          <p className={mc} style={{ maxWidth: 460, margin: "0 auto" }}>
            End-to-end software consulting structured around your goals.
          </p>
        </div>

        <div className="row g-4">
          {services.map((service) => (
            <div className="col-md-4" key={service.title}>
              <div className="h-100 p-4 rounded-3 card-hover" style={surface}>
                <div
                  className="service-icon"
                  style={{ background: service.iconBg, color: service.iconColor }}
                >
                  {service.icon}
                </div>
                <h4 className={`fw-bold mb-2 ${tc}`}>{service.title}</h4>
                <p className={`mb-0 ${mc}`} style={{ lineHeight: 1.7, fontSize: 18 }}>{service.body}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* ─── TECH STACK ────────────────────────────────────────── */}
      <div className="container pb-5">
        <hr className="gradient-divider" />
        <div className="text-center mb-4">
          <span className="section-label">Technology</span>
          <h3 className={`fw-bold ${tc}`}>Our tech stack</h3>
          <p className={mc} style={{ maxWidth: 420, margin: "0 auto" }}>
            Battle-tested tools across the full delivery lifecycle.
          </p>
        </div>
        <div className="d-flex flex-wrap gap-2 justify-content-center mt-4">
          {techStack.map((tech) => (
            <span
              key={tech.label}
              className="tech-badge"
              style={{
                background: tech.bg,
                color: tech.color,
                border: `1px solid ${tech.color}38`,
              }}
            >
              {tech.label}
            </span>
          ))}
        </div>
      </div>

      {/* ─── ABOUT (with photo) ────────────────────────────────── */}
      <About {...{ finalIsDarkMode }} />

      {/* ─── PROCESS + OUTCOMES ────────────────────────────────── */}
      <div className="container py-5">
        <div className="row g-4">

          <div className="col-md-6">
            <div className="h-100 p-5 rounded-3 card-hover" style={surface}>
              <span className="section-label">Process</span>
              <h2 className={`fw-bold mb-4 ${tc}`} style={{ fontSize: "1.75rem" }}>
                How we work
              </h2>
              {steps.map((step) => (
                <div key={step.title} className="process-step mb-4">
                  <div className="process-step-number">{step.num}</div>
                  <div>
                    <h5 className={`fw-bold mb-1 ${tc}`}>{step.title}</h5>
                    <p className={`mb-0 ${mc}`} style={{ lineHeight: 1.65 }}>{step.body}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="col-md-6">
            <div className="h-100 p-5 rounded-3 card-hover" style={surface}>
              <span className="section-label">Results</span>
              <h2 className={`fw-bold mb-4 ${tc}`} style={{ fontSize: "1.75rem" }}>
                Recent outcomes
              </h2>
              {outcomes.map((o) => (
                <div key={o.label} className="outcome-item">
                  <div className="outcome-icon">{o.icon}</div>
                  <div>
                    <div
                      className="fw-bold mb-1"
                      style={{ color: "#22d3ee", fontSize: "1rem" }}
                    >
                      {o.metric}
                    </div>
                    <div className={mc} style={{ fontSize: 14, lineHeight: 1.6 }}>{o.label}</div>
                  </div>
                </div>
              ))}
              <div className="mt-4 pt-2">
                <a
                  className="btn btn-outline-primary"
                  href="mailto:dilks44@gmail.com"
                  style={{ borderRadius: 8 }}
                >
                  Start your project &nbsp;<RocketOutlined />
                </a>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* ─── CTA BANNER ────────────────────────────────────────── */}
      <div className="container pb-5">
        <div className="cta-section text-center">
          <h2
            className="display-6 fw-bold text-white mb-3"
            style={{ position: "relative", zIndex: 1 }}
          >
            Ready to build something great?
          </h2>
          <p
            className="text-white-50 mb-5"
            style={{
              fontSize: "1.1rem",
              maxWidth: 480,
              margin: "0 auto 32px",
              lineHeight: 1.7,
              position: "relative",
              zIndex: 1,
            }}
          >
            Let's talk about your project. No obligation — just a focused
            conversation about where you're headed and how we can help.
          </p>
          <div
            className="d-flex gap-3 justify-content-center flex-wrap"
            style={{ position: "relative", zIndex: 1 }}
          >
            <a
              href="mailto:dilks44@gmail.com"
              className="btn btn-light btn-lg px-4"
              style={{ borderRadius: 10, fontWeight: 600, color: "#1e40af" }}
            >
              Schedule a consultation
            </a>
            <Link
              to="/contact"
              className="btn btn-outline-light btn-lg px-4"
              style={{ borderRadius: 10 }}
            >
              Contact us
            </Link>
          </div>
        </div>
      </div>

    </div>
  );
};
